import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import agent from '../api/axios';
import type { TeamMember, InviteUserRequest } from '../types/api';
import { useSnackbar } from 'notistack';
import { useRoleAccess } from '../hooks/useRoleAccess';

const inviteSchema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  role: yup
    .string()
    .oneOf(['Admin', 'Member', 'Viewer'])
    .required('Role is required'),
});

const editSchema = yup.object().shape({
  role: yup
    .string()
    .oneOf(['Admin', 'Member', 'Viewer'])
    .required('Role is required'),
  isActive: yup.boolean().required(),
});

export default function Team() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canManageTeam } = useRoleAccess();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inviteForm = useForm<InviteUserRequest>({
    resolver: yupResolver(inviteSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: 'Member',
    },
  });

  const editForm = useForm({
    resolver: yupResolver(editSchema),
    defaultValues: {
      role: 'Member' as 'Admin' | 'Member' | 'Viewer',
      isActive: true,
    },
  });

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await agent.Team.list();
      setMembers(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load team members:', err);
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleInviteOpen = () => {
    inviteForm.reset({
      email: '',
      firstName: '',
      lastName: '',
      role: 'Member',
    });
    setInviteDialogOpen(true);
  };

  const handleInviteClose = () => {
    setInviteDialogOpen(false);
  };

  const handleInviteSubmit = async (data: InviteUserRequest) => {
    setSubmitting(true);
    try {
      const response = await agent.Team.invite(data);
      if (response.success) {
        enqueueSnackbar('Invitation sent successfully!', { variant: 'success' });
        setInviteDialogOpen(false);
        fetchMembers();
      } else {
        enqueueSnackbar(response.message || 'Failed to send invitation', { variant: 'error' });
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to send invitation';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (member: TeamMember) => {
    setSelectedMember(member);
    editForm.reset({
      role: member.role,
      isActive: member.isActive,
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedMember(null);
  };

  const handleEditSubmit = async (data: { role: 'Admin' | 'Member' | 'Viewer'; isActive: boolean }) => {
    if (!selectedMember) return;

    setSubmitting(true);
    try {
      await agent.Team.update(selectedMember.id, {
        role: data.role,
        isActive: data.isActive,
      });
      enqueueSnackbar('Team member updated successfully', { variant: 'success' });
      setEditDialogOpen(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to update team member';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOpen = (member: TeamMember) => {
    setSelectedMember(member);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedMember(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMember) return;

    setSubmitting(true);
    try {
      await agent.Team.remove(selectedMember.id);
      enqueueSnackbar('Team member removed successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to remove team member';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async (member: TeamMember) => {
    try {
      const response = await agent.Team.resendInvite(member.id);
      if (response.success) {
        enqueueSnackbar('Invitation resent successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(response.message || 'Failed to resend invitation', { variant: 'error' });
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to resend invitation';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const getStatusChip = (member: TeamMember) => {
    if (member.isOwner) {
      return <Chip label="Owner" color="primary" size="small" />;
    }
    switch (member.status) {
      case 'active':
        return <Chip label="Active" color="success" size="small" />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'inactive':
        return <Chip label="Inactive" color="default" size="small" />;
      default:
        return null;
    }
  };

  const getRoleChip = (role: string) => {
    const colors: Record<string, 'error' | 'primary' | 'secondary'> = {
      Admin: 'error',
      Member: 'primary',
      Viewer: 'secondary',
    };
    return (
      <Chip label={role} color={colors[role] || 'default'} size="small" variant="outlined" />
    );
  };

  const getInitials = (member: TeamMember) => {
    return `${member.firstName?.charAt(0) || ''}${member.lastName?.charAt(0) || ''}`.toUpperCase() || '?';
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <Grid container spacing={2}>
      {members.map((member) => (
        <Grid item xs={12} key={member.id}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: member.isOwner ? 'primary.main' : 'secondary.main',
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {getInitials(member)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={600} noWrap>
                    {member.firstName} {member.lastName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {member.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {getRoleChip(member.role)}
                {getStatusChip(member)}
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Last login: {member.lastLogin
                  ? new Date(member.lastLogin).toLocaleDateString()
                  : member.status === 'pending'
                  ? 'Never'
                  : '-'}
              </Typography>

              {!member.isOwner && canManageTeam && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {member.status === 'pending' && (
                      <Button size="small" startIcon={<SendIcon />} onClick={() => handleResendInvite(member)}>
                        Resend
                      </Button>
                    )}
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditOpen(member)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteOpen(member)}>
                      Remove
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
      {members.length === 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              No team members yet. Invite someone to get started!
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  // Desktop Table View
  const renderDesktopTable = () => (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last Login</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell>
                {member.firstName} {member.lastName}
              </TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{getRoleChip(member.role)}</TableCell>
              <TableCell>{getStatusChip(member)}</TableCell>
              <TableCell>
                {member.lastLogin
                  ? new Date(member.lastLogin).toLocaleDateString()
                  : member.status === 'pending'
                  ? 'Never'
                  : '-'}
              </TableCell>
              <TableCell align="right">
                {!member.isOwner && canManageTeam && (
                  <>
                    {member.status === 'pending' && (
                      <Tooltip title="Resend Invitation">
                        <IconButton size="small" color="primary" onClick={() => handleResendInvite(member)}>
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => handleEditOpen(member)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleDeleteOpen(member)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
          {members.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="textSecondary" py={4}>
                  No team members yet. Invite someone to get started!
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <GroupsIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Team
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage team members
            </Typography>
          </Box>
        </Box>
        {canManageTeam && (
          <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} onClick={handleInviteOpen}>
            {isMobile ? 'Invite' : 'Invite Team Member'}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? renderMobileCards() : renderDesktopTable()}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleInviteClose} maxWidth="sm" fullWidth>
        <form onSubmit={inviteForm.handleSubmit(handleInviteSubmit)}>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Controller
                name="email"
                control={inviteForm.control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} label="Email" type="email" fullWidth error={!!error} helperText={error?.message} autoFocus />
                )}
              />
              <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                <Controller
                  name="firstName"
                  control={inviteForm.control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} label="First Name" fullWidth error={!!error} helperText={error?.message} />
                  )}
                />
                <Controller
                  name="lastName"
                  control={inviteForm.control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField {...field} label="Last Name" fullWidth error={!!error} helperText={error?.message} />
                  )}
                />
              </Box>
              <Controller
                name="role"
                control={inviteForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      <MenuItem value="Admin">Admin - Full access</MenuItem>
                      <MenuItem value="Member">Member - Manage payees and timesheets</MenuItem>
                      <MenuItem value="Viewer">Viewer - Read-only access</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleInviteClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
            >
              Send Invitation
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <form onSubmit={editForm.handleSubmit(handleEditSubmit)}>
          <DialogTitle>
            Edit Team Member
            {selectedMember && (
              <Typography variant="body2" color="textSecondary">
                {selectedMember.firstName} {selectedMember.lastName}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Controller
                name="role"
                control={editForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      <MenuItem value="Admin">Admin - Full access</MenuItem>
                      <MenuItem value="Member">Member - Manage payees and timesheets</MenuItem>
                      <MenuItem value="Viewer">Viewer - Read-only access</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="isActive"
                control={editForm.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Active"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{' '}
            <strong>
              {selectedMember?.firstName} {selectedMember?.lastName}
            </strong>{' '}
            from the team?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
